import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import 'webrtc-adapter';

interface CallState {
  isActive: boolean;
  isIncoming: boolean;
  isOutgoing: boolean;
  isConnected: boolean;
  isMuted: boolean;
  isVideoEnabled: boolean;
  isScreenSharing: boolean;
  callerId: string | null;
  calleeId: string | null;
  callerName: string | null;
  callType: 'audio' | 'video';
  callHistoryId: string | null;
  startTime: Date | null;
}

interface CallContextType {
  callState: CallState;
  startCall: (calleeId: string, calleeName: string, callType: 'audio' | 'video') => Promise<void>;
  acceptCall: () => Promise<void>;
  rejectCall: () => Promise<void>;
  endCall: () => void;
  toggleMute: () => void;
  toggleVideo: () => void;
  toggleScreenShare: () => Promise<void>;
  localVideoRef: React.RefObject<HTMLVideoElement>;
  remoteVideoRef: React.RefObject<HTMLVideoElement>;
  setLocalVideoRef: (ref: HTMLVideoElement | null) => void;
  setRemoteVideoRef: (ref: HTMLVideoElement | null) => void;
}

const CallContext = createContext<CallContextType | null>(null);

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
  { urls: 'stun:stun3.l.google.com:19302' },
  { urls: 'stun:stun4.l.google.com:19302' },
];

// Audio context for ringtone
let audioContext: AudioContext | null = null;
let ringInterval: number | null = null;

const playRingSound = () => {
  try {
    stopRingSound();
    audioContext = new AudioContext();
    const gainNode = audioContext.createGain();
    gainNode.connect(audioContext.destination);
    gainNode.gain.value = 0.15;

    const playTone = () => {
      if (!audioContext) return;
      
      // Pleasant two-tone ring
      const osc1 = audioContext.createOscillator();
      osc1.type = 'sine';
      osc1.frequency.value = 440;
      const osc1Gain = audioContext.createGain();
      osc1Gain.connect(gainNode);
      osc1.connect(osc1Gain);
      
      const now = audioContext.currentTime;
      osc1Gain.gain.setValueAtTime(0, now);
      osc1Gain.gain.linearRampToValueAtTime(0.3, now + 0.1);
      osc1Gain.gain.linearRampToValueAtTime(0, now + 0.4);
      osc1.start(now);
      osc1.stop(now + 0.5);

      setTimeout(() => {
        if (!audioContext) return;
        const osc2 = audioContext.createOscillator();
        osc2.type = 'sine';
        osc2.frequency.value = 349.23;
        const osc2Gain = audioContext.createGain();
        osc2Gain.connect(gainNode);
        osc2.connect(osc2Gain);
        
        const now2 = audioContext.currentTime;
        osc2Gain.gain.setValueAtTime(0, now2);
        osc2Gain.gain.linearRampToValueAtTime(0.25, now2 + 0.1);
        osc2Gain.gain.linearRampToValueAtTime(0, now2 + 0.5);
        osc2.start(now2);
        osc2.stop(now2 + 0.6);
      }, 150);
    };

    playTone();
    ringInterval = window.setInterval(playTone, 2500);
  } catch (e) {
    console.error('[Call] Ring error:', e);
  }
};

const stopRingSound = () => {
  if (ringInterval) {
    clearInterval(ringInterval);
    ringInterval = null;
  }
  if (audioContext) {
    audioContext.close().catch(() => {});
    audioContext = null;
  }
};

const vibrateDevice = () => {
  if ('vibrate' in navigator) {
    navigator.vibrate([200, 100, 200, 100, 200]);
  }
};

const stopVibration = () => {
  if ('vibrate' in navigator) {
    navigator.vibrate(0);
  }
};

const requestNotificationPermission = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    await Notification.requestPermission();
  }
};

const showCallNotification = (callerName: string, callType: 'audio' | 'video') => {
  if ('Notification' in window && Notification.permission === 'granted') {
    return new Notification(`${callType === 'video' ? '📹' : '📞'} Incoming call`, {
      body: `${callerName} is calling you`,
      icon: '/favicon.png',
      tag: 'incoming-call',
      requireInteraction: true,
    });
  }
  return null;
};

export function CallProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id;

  const [callState, setCallState] = useState<CallState>({
    isActive: false,
    isIncoming: false,
    isOutgoing: false,
    isConnected: false,
    isMuted: false,
    isVideoEnabled: true,
    isScreenSharing: false,
    callerId: null,
    calleeId: null,
    callerName: null,
    callType: 'audio',
    callHistoryId: null,
    startTime: null,
  });

  const peerConnection = useRef<RTCPeerConnection | null>(null);
  const localStream = useRef<MediaStream | null>(null);
  const screenStream = useRef<MediaStream | null>(null);
  const remoteStream = useRef<MediaStream | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const signalChannel = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const incomingNotification = useRef<Notification | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
    
    // Create hidden audio element for remote audio
    const audioEl = document.createElement('audio');
    audioEl.autoplay = true;
    audioEl.id = 'remote-call-audio';
    document.body.appendChild(audioEl);
    remoteAudioRef.current = audioEl;
    
    return () => {
      audioEl.remove();
    };
  }, []);

  const cleanup = useCallback(async () => {
    console.log('[Call] Cleaning up');
    stopRingSound();
    stopVibration();
    
    if (incomingNotification.current) {
      incomingNotification.current.close();
      incomingNotification.current = null;
    }

    if (callState.callHistoryId && callState.startTime) {
      const duration = Math.floor((Date.now() - callState.startTime.getTime()) / 1000);
      await supabase
        .from('call_history')
        .update({
          ended_at: new Date().toISOString(),
          duration_seconds: duration,
          status: callState.isConnected ? 'ended' : 'missed',
          had_screen_share: callState.isScreenSharing,
        })
        .eq('id', callState.callHistoryId);
    }

    if (screenStream.current) {
      screenStream.current.getTracks().forEach(track => track.stop());
      screenStream.current = null;
    }
    if (localStream.current) {
      localStream.current.getTracks().forEach(track => track.stop());
      localStream.current = null;
    }
    if (peerConnection.current) {
      peerConnection.current.close();
      peerConnection.current = null;
    }
    if (signalChannel.current) {
      supabase.removeChannel(signalChannel.current);
      signalChannel.current = null;
    }
    
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
    
    setCallState({
      isActive: false,
      isIncoming: false,
      isOutgoing: false,
      isConnected: false,
      isMuted: false,
      isVideoEnabled: true,
      isScreenSharing: false,
      callerId: null,
      calleeId: null,
      callerName: null,
      callType: 'audio',
      callHistoryId: null,
      startTime: null,
    });
  }, [callState.callHistoryId, callState.startTime, callState.isConnected, callState.isScreenSharing]);

  const initializePeerConnection = useCallback(async (isVideo: boolean) => {
    console.log('[Call] Initializing peer connection, video:', isVideo);
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    pc.onicecandidate = (event) => {
      if (event.candidate && signalChannel.current) {
        console.log('[Call] Sending ICE candidate');
        signalChannel.current.send({
          type: 'broadcast',
          event: 'ice-candidate',
          payload: { candidate: event.candidate, from: userId },
        });
      }
    };

    pc.ontrack = (event) => {
      console.log('[Call] Received track:', event.track.kind, event.track.id);
      
      // Always update to the latest stream
      if (!remoteStream.current) {
        remoteStream.current = new MediaStream();
      }
      
      // Add the new track to our stream
      const existingTrack = remoteStream.current.getTracks().find(t => t.kind === event.track.kind);
      if (existingTrack) {
        remoteStream.current.removeTrack(existingTrack);
      }
      remoteStream.current.addTrack(event.track);
      
      // Set up audio playback
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = remoteStream.current;
        remoteAudioRef.current.play().catch(e => console.log('[Call] Audio autoplay blocked:', e));
      }
      
      // Set up video - always update when we get a video track
      if (event.track.kind === 'video' && remoteVideoRef.current) {
        console.log('[Call] Setting remote video stream');
        remoteVideoRef.current.srcObject = remoteStream.current;
        remoteVideoRef.current.play().catch(e => console.log('[Call] Video autoplay blocked:', e));
      }
    };
    
    // Handle renegotiation when tracks are added
    pc.onnegotiationneeded = async () => {
      console.log('[Call] Negotiation needed');
      if (pc.signalingState === 'stable' && signalChannel.current) {
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          const partnerId = callState.callerId === userId ? callState.calleeId : callState.callerId;
          signalChannel.current.send({
            type: 'broadcast',
            event: 'offer',
            payload: { offer, to: partnerId, from: userId },
          });
        } catch (e) {
          console.error('[Call] Renegotiation error:', e);
        }
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('[Call] Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        setCallState(prev => ({ 
          ...prev, 
          isConnected: true,
          startTime: new Date(),
        }));
        stopRingSound();
        stopVibration();
        toast.success('Call connected');
      } else if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        toast.error('Call disconnected');
        cleanup();
      }
    };

    try {
      console.log('[Call] Requesting user media');

      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };

      const requestMedia = (video: boolean) =>
        navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
          video,
        });

      let stream: MediaStream;

      try {
        stream = await requestMedia(isVideo);
      } catch (error) {
        // If camera fails for a video call, fall back to audio-only instead of failing the entire call.
        if (isVideo) {
          console.warn('[Call] Video capture failed, falling back to audio-only:', error);
          toast.warning('Camera unavailable — joining with audio only');
          stream = await requestMedia(false);
        } else {
          throw error;
        }
      }

      localStream.current = stream;

      stream.getTracks().forEach(track => {
        console.log('[Call] Adding local track:', track.kind);
        pc.addTrack(track, stream);
      });

      // Reflect actual media state (e.g., video call requested but camera unavailable)
      setCallState(prev => ({
        ...prev,
        isVideoEnabled: stream.getVideoTracks().length > 0,
      }));

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('[Call] Media error:', error);
      toast.error('Could not access microphone');
      cleanup();
      return null;
    }

    peerConnection.current = pc;
    return pc;
  }, [userId, cleanup]);

  const setupSignaling = useCallback((partnerId: string) => {
    const channelName = `call-${[userId, partnerId].sort().join('-')}`;
    console.log('[Call] Setting up signaling channel:', channelName);
    
    const channel = supabase.channel(channelName)
      .on('broadcast', { event: 'offer' }, async ({ payload }) => {
        console.log('[Call] Received offer from:', payload.from);
        if (payload.to === userId && peerConnection.current) {
          await peerConnection.current.setRemoteDescription(new RTCSessionDescription(payload.offer));
          const answer = await peerConnection.current.createAnswer();
          await peerConnection.current.setLocalDescription(answer);
          channel.send({
            type: 'broadcast',
            event: 'answer',
            payload: { answer, to: payload.from, from: userId },
          });
        }
      })
      .on('broadcast', { event: 'answer' }, async ({ payload }) => {
        console.log('[Call] Received answer from:', payload.from);
        if (payload.to === userId && peerConnection.current) {
          await peerConnection.current.setRemoteDescription(new RTCSessionDescription(payload.answer));
        }
      })
      .on('broadcast', { event: 'ice-candidate' }, async ({ payload }) => {
        if (payload.from !== userId && peerConnection.current) {
          try {
            await peerConnection.current.addIceCandidate(new RTCIceCandidate(payload.candidate));
          } catch (error) {
            console.error('[Call] ICE error:', error);
          }
        }
      })
      .on('broadcast', { event: 'call-accepted' }, async ({ payload }) => {
        console.log('[Call] Call accepted by:', payload.from);
        if (payload.to === userId && peerConnection.current) {
          stopRingSound();
          
          if (callState.callHistoryId) {
            await supabase
              .from('call_history')
              .update({ status: 'answered', answered_at: new Date().toISOString() })
              .eq('id', callState.callHistoryId);
          }
          
          const offer = await peerConnection.current.createOffer();
          await peerConnection.current.setLocalDescription(offer);
          channel.send({
            type: 'broadcast',
            event: 'offer',
            payload: { offer, to: payload.from, from: userId },
          });
        }
      })
      .on('broadcast', { event: 'call-rejected' }, async ({ payload }) => {
        if (payload.to === userId) {
          if (callState.callHistoryId) {
            await supabase
              .from('call_history')
              .update({ status: 'rejected', ended_at: new Date().toISOString() })
              .eq('id', callState.callHistoryId);
          }
          toast.info('Call was declined');
          cleanup();
        }
      })
      .on('broadcast', { event: 'call-ended' }, ({ payload }) => {
        if (payload.to === userId || payload.from !== userId) {
          toast.info('Call ended');
          cleanup();
        }
      })
      .on('broadcast', { event: 'screen-share-started' }, ({ payload }) => {
        if (payload.from !== userId) {
          console.log('[Call] Remote user started screen sharing');
        }
      })
      .subscribe();

    signalChannel.current = channel;
    return channel;
  }, [userId, cleanup, callState.callHistoryId]);

  const startCall = useCallback(async (calleeId: string, calleeName: string, callType: 'audio' | 'video') => {
    if (!userId) return;
    console.log('[Call] Starting call to:', calleeId, 'type:', callType);

    const pc = await initializePeerConnection(callType === 'video');
    if (!pc) return;

    const { data: callRecord } = await supabase
      .from('call_history')
      .insert({
        caller_id: userId,
        callee_id: calleeId,
        call_type: callType,
        status: 'missed',
      })
      .select()
      .single();

    setupSignaling(calleeId);

    const { data: profile } = await supabase
      .from('profiles')
      .select('display_name')
      .eq('user_id', userId)
      .single();

    setCallState({
      isActive: true,
      isIncoming: false,
      isOutgoing: true,
      isConnected: false,
      isMuted: false,
      isVideoEnabled: callType === 'video' && (localStream.current?.getVideoTracks().length ?? 0) > 0,
      isScreenSharing: false,
      callerId: userId,
      calleeId,
      callerName: calleeName,
      callType,
      callHistoryId: callRecord?.id || null,
      startTime: null,
    });

    playRingSound();

    // Send via global channel
    const globalChannel = supabase.channel('global-calls');
    await globalChannel.subscribe();
    
    globalChannel.send({
      type: 'broadcast',
      event: 'call-request',
      payload: {
        from: userId,
        to: calleeId,
        callerName: profile?.display_name || 'Unknown',
        callType,
        callHistoryId: callRecord?.id,
      },
    });

    toast.info(`Calling ${calleeName}...`);
  }, [userId, initializePeerConnection, setupSignaling]);

  const acceptCall = useCallback(async () => {
    if (!callState.callerId || !userId) return;
    console.log('[Call] Accepting call from:', callState.callerId);

    stopRingSound();
    stopVibration();
    if (incomingNotification.current) {
      incomingNotification.current.close();
    }

    const pc = await initializePeerConnection(callState.callType === 'video');
    if (!pc) return;

    const channel = setupSignaling(callState.callerId);

    if (callState.callHistoryId) {
      await supabase
        .from('call_history')
        .update({ status: 'answered', answered_at: new Date().toISOString() })
        .eq('id', callState.callHistoryId);
    }

    setCallState(prev => ({
      ...prev,
      isIncoming: false,
      isOutgoing: false,
      isVideoEnabled: (localStream.current?.getVideoTracks().length ?? 0) > 0,
    }));

    channel.send({
      type: 'broadcast',
      event: 'call-accepted',
      payload: { from: userId, to: callState.callerId },
    });
  }, [userId, callState.callerId, callState.callType, callState.callHistoryId, initializePeerConnection, setupSignaling]);

  const rejectCall = useCallback(async () => {
    console.log('[Call] Rejecting call');
    stopRingSound();
    stopVibration();
    if (incomingNotification.current) {
      incomingNotification.current.close();
    }

    if (callState.callHistoryId) {
      await supabase
        .from('call_history')
        .update({ status: 'rejected', ended_at: new Date().toISOString() })
        .eq('id', callState.callHistoryId);
    }

    if (signalChannel.current && callState.callerId && userId) {
      signalChannel.current.send({
        type: 'broadcast',
        event: 'call-rejected',
        payload: { from: userId, to: callState.callerId },
      });
    }

    cleanup();
  }, [userId, callState.callerId, callState.callHistoryId, cleanup]);

  const endCall = useCallback(() => {
    console.log('[Call] Ending call');
    stopRingSound();
    stopVibration();
    
    if (signalChannel.current) {
      const partnerId = callState.callerId === userId ? callState.calleeId : callState.callerId;
      signalChannel.current.send({
        type: 'broadcast',
        event: 'call-ended',
        payload: { from: userId, to: partnerId },
      });
    }
    cleanup();
  }, [userId, callState.callerId, callState.calleeId, cleanup]);

  const toggleMute = useCallback(() => {
    if (localStream.current) {
      const audioTrack = localStream.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setCallState(prev => ({ ...prev, isMuted: !audioTrack.enabled }));
      }
    }
  }, []);

  const toggleVideo = useCallback(() => {
    if (localStream.current) {
      const videoTrack = localStream.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setCallState(prev => ({ ...prev, isVideoEnabled: videoTrack.enabled }));
      }
    }
  }, []);

  const toggleScreenShare = useCallback(async () => {
    if (!peerConnection.current) return;

    if (callState.isScreenSharing) {
      console.log('[Call] Stopping screen share');
      if (screenStream.current) {
        screenStream.current.getTracks().forEach(track => {
          track.stop();
          const sender = peerConnection.current?.getSenders().find(s => s.track === track);
          if (sender) peerConnection.current?.removeTrack(sender);
        });
        screenStream.current = null;
      }

      // Restore camera video if it was a video call
      if (localStream.current && callState.callType === 'video') {
        const videoTrack = localStream.current.getVideoTracks()[0];
        if (videoTrack) {
          const existingSender = peerConnection.current.getSenders().find(s => s.track?.kind === 'video');
          if (existingSender) {
            existingSender.replaceTrack(videoTrack);
          } else {
            peerConnection.current.addTrack(videoTrack, localStream.current);
          }
        }
      }

      if (localVideoRef.current && localStream.current) {
        localVideoRef.current.srcObject = localStream.current;
      }

      setCallState(prev => ({ ...prev, isScreenSharing: false }));
    } else {
      try {
        console.log('[Call] Starting screen share');
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: { 
            width: { ideal: 1920, max: 1920 }, 
            height: { ideal: 1080, max: 1080 },
            frameRate: { ideal: 30, max: 30 }
          },
          audio: true,
        });
        
        screenStream.current = stream;
        
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          console.log('[Call] Adding screen video track');
          const existingVideoSender = peerConnection.current.getSenders().find(s => s.track?.kind === 'video');
          if (existingVideoSender) {
            // Replace existing video track
            await existingVideoSender.replaceTrack(videoTrack);
          } else {
            // No video sender exists (audio call), add new track - this triggers renegotiation
            peerConnection.current.addTrack(videoTrack, stream);
          }
        }
        
        // Add screen audio if available
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          console.log('[Call] Adding screen audio track');
          peerConnection.current.addTrack(audioTrack, stream);
        }

        // Notify peer that screen share started
        if (signalChannel.current) {
          const partnerId = callState.callerId === userId ? callState.calleeId : callState.callerId;
          signalChannel.current.send({
            type: 'broadcast',
            event: 'screen-share-started',
            payload: { from: userId, to: partnerId },
          });
        }

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }

        videoTrack.onended = () => {
          console.log('[Call] Screen share track ended');
          toggleScreenShare();
        };

        setCallState(prev => ({ ...prev, isScreenSharing: true }));
        toast.success('Screen sharing started');
      } catch (error) {
        console.error('[Call] Screen share error:', error);
        if ((error as Error).name !== 'NotAllowedError') {
          toast.error('Could not start screen sharing');
        }
      }
    }
  }, [callState.isScreenSharing, callState.callType, callState.callerId, callState.calleeId, userId]);

  // Listen for incoming calls globally
  useEffect(() => {
    if (!userId) return;
    console.log('[Call] Setting up global call listener for:', userId);

    const channel = supabase.channel('global-calls', {
      config: { presence: { key: userId } }
    })
      .on('broadcast', { event: 'call-request' }, async ({ payload }) => {
        console.log('[Call] Received call-request:', payload);
        if (payload.to === userId && !callState.isActive) {
          console.log('[Call] Incoming call from:', payload.from);
          playRingSound();
          vibrateDevice();
          incomingNotification.current = showCallNotification(payload.callerName, payload.callType);
          
          setCallState(prev => ({
            ...prev,
            isActive: true,
            isIncoming: true,
            callerId: payload.from,
            callerName: payload.callerName,
            callType: payload.callType,
            callHistoryId: payload.callHistoryId,
          }));
          setupSignaling(payload.from);
        }
      })
      .subscribe((status) => {
        console.log('[Call] Global channel status:', status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, callState.isActive, setupSignaling]);

  return (
    <CallContext.Provider value={{
      callState,
      startCall,
      acceptCall,
      rejectCall,
      endCall,
      toggleMute,
      toggleVideo,
      toggleScreenShare,
      localVideoRef: localVideoRef as React.RefObject<HTMLVideoElement>,
      remoteVideoRef: remoteVideoRef as React.RefObject<HTMLVideoElement>,
      setLocalVideoRef: (ref) => { localVideoRef.current = ref; },
      setRemoteVideoRef: (ref) => { remoteVideoRef.current = ref; },
    }}>
      {children}
    </CallContext.Provider>
  );
}

export function useCall() {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
}
